import React, { useState } from 'react';
import { ChevronDown, Palette, Gamepad2, Building, Sword } from 'lucide-react';
import { useNintendoQuestTheme } from '../contexts/NintendoQuestThemeContext';
import { ThemeName, THEME_CONFIGS } from '../types/theme.types';

const ThemeSelector: React.FC = () => {
  const { currentTheme, setTheme, themeConfig } = useNintendoQuestTheme();
  const [isOpen, setIsOpen] = useState(false);

  const getThemeIcon = (themeName: ThemeName) => {
    switch (themeName) {
      case 'default':
        return <Gamepad2 className="w-4 h-4" />;
      case 'lego-classic':
        return <Building className="w-4 h-4" />;
      case 'lego-ninjago':
        return <Sword className="w-4 h-4" />;
      default:
        return <Palette className="w-4 h-4" />;
    }
  };

  const handleThemeSelect = (themeName: ThemeName) => {
    setTheme(themeName);
    setIsOpen(false);
  };

  // Get theme-specific classes
  const getDropdownClasses = () => {
    switch (currentTheme) {
      case 'lego-classic':
        return {
          dropdown: 'bg-white/98 border-red-500 text-slate-800',
          button: 'text-slate-800 hover:bg-red-50',
          selected: 'bg-red-100'
        };
      case 'lego-ninjago':
        return {
          dropdown: 'bg-black/95 border-yellow-500 text-yellow-400',
          button: 'text-yellow-400 hover:bg-gray-800',
          selected: 'bg-yellow-500/20'
        };
      default:
        return {
          dropdown: 'bg-white/95 border-gray-200 text-gray-900',
          button: 'text-gray-900 hover:bg-gray-50',
          selected: 'bg-blue-50'
        };
    }
  };

  const classes = getDropdownClasses();

  return (
    <div className="relative">
      {/* Current Theme Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 rounded-lg border bg-white/10 backdrop-blur-md border-white/20 hover:border-white/40 transition-all duration-200 text-white shadow-sm hover:shadow-md"
        aria-label="Select theme"
      >
        {getThemeIcon(currentTheme)}
        <span className="font-medium">{themeConfig.displayName}</span>
        <ChevronDown 
          className={`w-4 h-4 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Theme Options */}
          <div className={`absolute top-full mt-2 right-0 z-20 backdrop-blur-md border-2 rounded-xl shadow-xl min-w-[280px] max-w-[320px] overflow-hidden ${classes.dropdown}`}>
            {Object.entries(THEME_CONFIGS).map(([themeName, config]) => (
              <button
                key={themeName}
                onClick={() => handleThemeSelect(themeName as ThemeName)}
                className={`w-full text-left px-4 py-3 transition-colors duration-150 flex items-start gap-3 ${classes.button} ${
                  currentTheme === themeName ? classes.selected : ''
                }`}
              >
                <div className={`p-2 rounded-lg mt-0.5 bg-${config.colors.primary}/20 text-${config.colors.primary}`}>
                  {getThemeIcon(themeName as ThemeName)}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">
                      {config.displayName}
                    </span>
                    {config.targetAudience === 'child' && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">
                        Kid-Friendly
                      </span>
                    )}
                  </div>
                  <p className="text-sm mt-1 opacity-80">
                    {config.description}
                  </p>
                  
                  {/* Theme Preview Pills */}
                  <div className="flex gap-1 mt-2">
                    <div className={`w-3 h-3 rounded-full bg-${config.colors.primary}`} />
                    <div className={`w-3 h-3 rounded-full bg-${config.colors.secondary}`} />
                    <div className={`w-3 h-3 rounded-full bg-${config.colors.accent}`} />
                  </div>
                </div>
              </button>
            ))}
            
            {/* Theme Tips */}
            <div className={`px-4 py-3 border-t opacity-60 ${currentTheme === 'lego-ninjago' ? 'border-yellow-500/30' : 'border-gray-200'}`}>
              <p className="text-xs">
                💡 <strong>Kid themes</strong> show fewer details to reduce overwhelm.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ThemeSelector;